<template>
  <div>
    <v-card
      outlined
      class="pa-3 mb-3"
    >
      <v-autocomplete
        v-model="selected"
        :items="results"
        :loading="searching"
        :search-input.sync="search"
        item-value="id"
        :item-text="resultText"
        return-object
        no-filter
        hide-no-data
        clearable
        outlined
        dense
        hide-details
        prepend-inner-icon="mdi-magnify"
        label="ຄົ້ນຫາລູກຄ້າ: ຊື່, ເບີໂທ, CUS_ / COM_"
        @change="pick"
      >
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title>
              <span class="primary--text font-weight-medium mr-2">{{ item.code }}</span>{{ item.name }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ item.phone || '-' }} · {{ typeName(item.type) }} · {{ item.status }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </v-card>

    <template v-if="customer">
      <v-card
        outlined
        class="pa-3 mb-3 d-flex align-center"
      >
        <v-avatar
          color="blue lighten-5"
          class="mr-3"
        >
          <v-icon color="primary">
            {{ customer.type === 'company' ? 'mdi-office-building' : 'mdi-home' }}
          </v-icon>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="font-weight-medium">
            {{ customer.code }} · {{ customer.name }}
          </div>
          <div class="text-caption grey--text text--darken-1">
            {{ customer.phone || '-' }} · {{ customer.village || '-' }} · {{ typeName(customer.type) }}
            <span v-if="customer.fix_cost"> · {{ Number(customer.fix_cost).toLocaleString() }}</span>
          </div>
        </div>
        <v-chip
          small
          :color="customer.status === 'inactive' ? 'red lighten-5' : 'green lighten-5'"
          :text-color="customer.status === 'inactive' ? 'red darken-2' : 'green darken-2'"
        >
          {{ customer.status }}
        </v-chip>
      </v-card>

      <div class="d-flex flex-wrap align-center mb-3">
        <v-chip
          v-for="c in categories"
          :key="c.key"
          small
          class="mr-2 mb-2"
          :color="category === c.key ? 'primary' : undefined"
          :outlined="category !== c.key"
          @click="setCategory(c.key)"
        >
          {{ c.name }} {{ counts[c.key] || 0 }}
        </v-chip>
        <v-spacer />
        <v-text-field
          v-model="dateFrom"
          type="date"
          dense
          outlined
          hide-details
          label="ແຕ່ວັນທີ"
          style="max-width: 170px"
          class="mr-2 mb-2"
          @change="load(1)"
        />
        <v-text-field
          v-model="dateTo"
          type="date"
          dense
          outlined
          hide-details
          label="ຫາວັນທີ"
          style="max-width: 170px"
          class="mb-2"
          @change="load(1)"
        />
      </div>

      <v-card outlined>
        <v-progress-linear
          v-if="loading"
          indeterminate
        />
        <div
          v-if="!loading && !items.length"
          class="pa-6 text-center grey--text"
        >
          ບໍ່ມີປະຫວັດໃນຊ່ວງນີ້
        </div>
        <template v-for="group in grouped">
          <div
            :key="'d' + group.day"
            class="px-4 pt-3 pb-1 text-caption grey--text"
          >
            {{ group.day }}
          </div>
          <div
            v-for="item in group.items"
            :key="item.id"
            class="d-flex px-4 py-3 activity-row"
          >
            <div
              class="text-body-2 grey--text text--darken-1"
              style="width: 52px; flex-shrink: 0"
            >
              {{ item.at.slice(11, 16) }}
            </div>
            <div class="flex-grow-1">
              <div class="text-body-2">
                <v-chip
                  x-small
                  label
                  class="mr-2"
                  :color="actionColor(item.action).bg"
                  :text-color="actionColor(item.action).text"
                >
                  {{ actionName(item.action) }}
                </v-chip>
                {{ item.subject }}
              </div>
              <div
                v-for="ch in (item.action === 'updated' ? item.changes : item.summary)"
                :key="ch.field"
                class="text-body-2 mt-1"
              >
                <span class="grey--text text--darken-1">{{ ch.label }}:</span>
                <template v-if="item.action === 'updated'">
                  <span class="red--text text-decoration-line-through ml-1">{{ ch.old === null ? '-' : ch.old }}</span>
                  <v-icon x-small>
                    mdi-arrow-right
                  </v-icon>
                  <span class="green--text text--darken-2">{{ ch.new === null ? '-' : ch.new }}</span>
                </template>
                <span
                  v-else
                  class="ml-1"
                >{{ ch.new }}</span>
              </div>
              <div class="text-caption grey--text mt-1">
                <v-icon x-small>
                  mdi-account
                </v-icon>
                {{ item.causer }}
                <a
                  class="ml-3"
                  @click="toggle(item.id)"
                >
                  {{ open[item.id] ? 'ເຊື່ອງລາຍລະອຽດ' : 'ເບິ່ງລາຍລະອຽດທັງໝົດ' }}
                </a>
              </div>
              <pre
                v-if="open[item.id]"
                class="raw mt-2"
              >{{ JSON.stringify(item.raw, null, 2) }}</pre>
            </div>
          </div>
        </template>
      </v-card>
      <v-pagination
        v-if="pagination.last_page > 1"
        v-model="page"
        :length="pagination.last_page"
        :total-visible="7"
        class="mt-3"
        @input="load"
      />
    </template>

    <div
      v-else
      class="pa-6 text-center grey--text"
    >
      ພິມ ຊື່, ເບີໂທ ຫຼື ລະຫັດລູກຄ້າ ເພື່ອເບິ່ງປະຫວັດ
    </div>
  </div>
</template>

<script>
const CATEGORIES = [
  { key: 'all', name: 'ທັງໝົດ' },
  { key: 'customer', name: 'ຂໍ້ມູນລູກຄ້າ' },
  { key: 'bill', name: 'ບິນ' },
  { key: 'payment', name: 'ການຈ່າຍ' },
  { key: 'collection', name: 'ການເກັບ / ແຜນເສັ້ນທາງ' },
  { key: 'approval', name: 'ອະນຸມັດ' },
];
const ACTIONS = {
  created: { name: 'ສ້າງ', bg: 'blue lighten-5', text: 'blue darken-2' },
  updated: { name: 'ແກ້ໄຂ', bg: 'amber lighten-5', text: 'amber darken-4' },
  deleted: { name: 'ລຶບ', bg: 'red lighten-5', text: 'red darken-2' },
  restored: { name: 'ກູ້ຄືນ', bg: 'green lighten-5', text: 'green darken-2' },
};

export default {
  name: 'CustomerHistory',
  data() {
    return {
      search: null,
      results: [],
      searching: false,
      searchSeq: 0,
      searchTimer: null,
      selected: null,
      customerId: null,
      customer: null,
      categories: CATEGORIES,
      category: 'all',
      counts: {},
      dateFrom: '',
      dateTo: '',
      items: [],
      loading: false,
      page: 1,
      pagination: {},
      open: {},
    };
  },
  computed: {
    grouped() {
      const groups = [];
      this.items.forEach((item) => {
        const day = item.at.slice(0, 10);
        if (!groups.length || groups[groups.length - 1].day !== day) groups.push({ day, items: [] });
        groups[groups.length - 1].items.push(item);
      });
      return groups;
    },
  },
  watch: {
    search(value) {
      clearTimeout(this.searchTimer);
      if (!value || value.length < 2 || (this.selected && value === this.resultText(this.selected))) return;
      this.searchTimer = setTimeout(() => this.find(value), 300);
    },
  },
  created() {
    // Deep link: /activity-log?customer=123
    if (this.$route.query.customer) this.openCustomer(this.$route.query.customer);
  },
  methods: {
    resultText(item) {
      return item ? `${item.code} · ${item.name} · ${item.phone || ''}` : '';
    },
    typeName(type) {
      return type === 'company' ? 'ທຸລະກິດ' : 'ຄົວເຮືອນ';
    },
    actionName(action) {
      return (ACTIONS[action] || ACTIONS.updated).name;
    },
    actionColor(action) {
      return ACTIONS[action] || ACTIONS.updated;
    },
    find(q) {
      const seq = ++this.searchSeq;
      this.searching = true;
      this.$axios
        .get('activity/customers', { params: { q } })
        .then((res) => {
          if (seq === this.searchSeq) this.results = res.data.data || [];
        })
        .catch(() => {})
        .finally(() => {
          if (seq === this.searchSeq) this.searching = false;
        });
    },
    pick(item) {
      if (!item) {
        this.customer = null;
        this.items = [];
        return;
      }
      this.openCustomer(item.id);
    },
    openCustomer(id) {
      this.customerId = id;
      this.category = 'all';
      this.load(1);
    },
    setCategory(key) {
      this.category = key;
      this.load(1);
    },
    toggle(id) {
      this.$set(this.open, id, !this.open[id]);
    },
    load(page) {
      if (!this.customerId) return;
      this.page = page || 1;
      this.loading = true;
      this.$axios
        .get(`activity/customer/${this.customerId}`, {
          params: {
            page: this.page,
            category: this.category,
            date_from: this.dateFrom || undefined,
            date_to: this.dateTo || undefined,
          },
        })
        .then((res) => {
          const data = res.data.data;
          this.customer = data.customer;
          this.counts = data.counts || {};
          this.items = data.data || [];
          this.pagination = data.pagination || {};
          this.open = {};
        })
        .catch((error) => {
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: (error.response && error.response.data && error.response.data.message) || 'Something went wrong',
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.activity-row {
  border-top: 0.5px solid rgba(0, 0, 0, 0.08);
}
.raw {
  font-size: 12px;
  background: rgba(0, 0, 0, 0.04);
  padding: 8px;
  border-radius: 6px;
  max-height: 260px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
